import React, { useMemo, useRef, useState } from "react";
import { apiClient, useApiClient } from "../../../shared/customHooks";
import { applicationChecklistForm, ChecklistEmailStatus } from "../../../shared/formObjects/application/applicationChecklistForm";
import { Modal, ModalRef } from "../../modal/modal";
import { useApplicationContext } from "../context/applicationContext";
import { ToastInfo } from "../../toaster/toaster";
import { eventEmitter } from "../../../shared/event";
import { useNavigate } from "react-router";
import { FormikProps } from "formik";
import BaseApplicationComponent from "../../../shared/baseComponent/baseWrappers/BaseApplicationComponent";
import { useGlobalContext } from "../../../shared/GlobalContext";

function ApplicationChecklistComponent({ disabled, data, index }: any) {
  const { fetchData, postData, putData } = useApiClient(`ApplicationChecklists`);
  const identityApi = useApiClient("UserLink", { identity: true });
  const [rest, setRest] = useState<any>({ checklists: [] });
  const [emailDetails, setEmailDetails] = useState<any[]>([]);
  const [currentData, setCurrentData] = useState<any>(null);
  const [f, setFormik] = useState<FormikProps<any> | null>(null);
  const [newForm, setNewForm] = useState(true);
  const modalRef = useRef<ModalRef | null>(null);
  const navigate = useNavigate();
  const {
    id,
    application: { hasAppeal },
  } = useApplicationContext();
  const { accountInfo } = useGlobalContext();

  const form = useMemo(() => {
    return applicationChecklistForm(triggerModal, triggerNoIssuesFoundEmail);
  }, [modalRef.current]);

  const api: apiClient = useMemo(
    () => ({
      fetchData: async (route?: string, params?: any) =>
        await fetchData(route, params).then((res) => {
          setRest(res.data);
          res.data = res.data.checklists[0];
          return res;
        }),
      putData: async (formData: any, route?: string) => {
        const restData = { ...rest };
        restData.checklists[0] = formData;
        return await putData(restData, route);
      },
      postData: async (formData: any, route?: string) => {
        const restData = { ...rest };
        restData.checklists[0] = formData;
        return await postData(restData, route);
      },
    }),
    [rest]
  );

  function getInitalValues(initialData: any, formik: FormikProps<any>) {
    setNewForm(initialData.emailStatus == null || initialData.emailStatus === ChecklistEmailStatus.NoEmailSent);
    setFormik(formik);
    const today = new Date();
    const defaultFutureDate = new Date();
    defaultFutureDate.setDate(
      today.getDate() + (initialData.emailStatus == null || initialData.emailStatus === ChecklistEmailStatus.NoEmailSent ? 14 : 7)
    );

    if (
      (initialData.emailStatus == null || initialData.emailStatus === ChecklistEmailStatus.NoEmailSent) &&
      !initialData?.emailGroup?.firstEmailDueDate
    ) {
      formik.setFieldValue("emailGroup.firstEmailDueDate", defaultFutureDate);
    } else if (initialData.emailStatus === ChecklistEmailStatus.InitialEmail && !initialData?.emailGroup?.firstReminderEmailDueDate) {
      formik.setFieldValue("emailGroup.firstReminderEmailDueDate", defaultFutureDate);
    } else if (initialData.emailStatus === ChecklistEmailStatus.FirstReminder && !initialData?.emailGroup?.finalReminderEmailDueDate) {
      formik.setFieldValue("emailGroup.finalReminderEmailDueDate", defaultFutureDate);
    }
  }

  function triggerModal(newData: any) {
    setCurrentData(newData);
    f?.validateForm().then((value) => {
      if (JSON.stringify(value) === "{}") {
        postData(newData, `preview/${id}`).then((res) => {
          setEmailDetails(res.data);
          modalRef.current?.toggle();
        });
      } else {
        f.setTouched(touchAllFields(value));
      }
    });
  }

  async function exitModal() {
    await confirmEmail(currentData);
  }

  async function triggerNoIssuesFoundEmail(newData: any) {
    f?.validateForm().then(async (value) => {
      if (JSON.stringify(value) === "{}") {
        await confirmEmail(newData);
      } else {
        f.setTouched(touchAllFields(value));
      }
    });
  }

  async function confirmEmail(data: any) {
    await identityApi
      .fetchData(`GetAuthorisedUserEmails`, { linkId: id.toString() })
      .then((res) => {
        data.authorisedUserEmails = res.data;
        postData(data, `SubmitEmailChecklistCurrent/${id}`)
          .then(() => {
            const data: ToastInfo = { title: "Success", message: "Email Submission Successful!", severity: "success" };
            eventEmitter.emit("tstr", data);
            const targetPath = `/applications/ApplicationBaseCurrent/${id}`;
            navigate(targetPath);
          })
          .catch(() => {
            const errorData: ToastInfo = {
              title: "Error",
              message: "Could not submit email. Please contact support",
              severity: "error",
            };
            eventEmitter.emit("tstr", errorData);
          });
      })
      .catch(() => {
        const errorData: ToastInfo = {
          title: "Error",
          message: "Could not locate any authorised users. Please contact support",
          severity: "error",
        };
        eventEmitter.emit("tstr", errorData);
      });
  }

  function touchAllFields(obj: any): any {
    if (typeof obj !== "object" || obj === null) {
      return true;
    }
    const touched: any = {};
    for (const key of Object.keys(obj)) {
      touched[key] = touchAllFields(obj[key]);
    }
    return touched;
  }

  function createNewChecklist() {
    postData(null, `addChecklist/${id}`).then(() => {
      const data: ToastInfo = { title: "Success", message: "Checklist Created!", severity: "success" };
      eventEmitter.emit("tstr", data);
      navigate(`/applications/ApplicationBaseCurrent/${id}`);
    });
  }

  return (
    <>
      {" "}
      <BaseApplicationComponent
        formBody={form}
        headerLabel="Application Checklist"
        editPath="applicationchecklist"
        submitBtnTitle={newForm ? "Save" : "Create New Checklist"}
        customSubmit={newForm ? undefined : createNewChecklist}
        apiClient={api}
        disabled={disabled}
        data={data}
        showEdit={index == 0 && !hasAppeal && accountInfo.isInternal}
        isNavHidden={index > 0}
        formContext={accountInfo}
        getInitialValues={getInitalValues}
        id={id}
      />
      <Modal ref={modalRef} confirmAction={exitModal} headerLabel="Submit & Send Email Confirmation" confirmLabel="Submit & Send Email">
        <div className="email-table">
          <p className="text-left">
            If you submit this form, the system will update the status of this application and send an email to the kindergarten requesting the
            following items on the checklist are addressed
          </p>
          <div style={{ textAlign: "left", padding: "1em" }}>
            <ul className="pl-0">
              {emailDetails.map((x: { message: string; facilitatorDescription: string }, index: number) => (
                <li key={index} className="list-none">
                  <table style={{ tableLayout: "fixed", width: "100%", padding: "0px" }}>
                    <tr className="border-bottom-1">
                      <td valign="top" className="checklist">
                        {x.message}
                      </td>
                      <td valign="top">{x.facilitatorDescription}</td>
                    </tr>
                  </table>
                </li>
              ))}
            </ul>
            <p style={{ whiteSpace: "pre-line" }}>{currentData?.summary}</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ApplicationChecklistComponent;
